import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { Environment } from 'src/Environment';
import { TransactionFilterContext } from 'src/context/transaction-filter-context';
import { SelectedTransactionContext } from 'src/context/selected-transaction-context';
import { CactusImage } from 'src/assets/images';
import { FallenIceCream } from 'src/assets/images';
import { DateTime } from 'luxon';
import { formatCurrencyAmountSimple } from 'src/helpers/formatting';
import { StatusSpan, Card, ShowIf, LoaderAnimation } from 'src/components';
import { DownloadCSVContext } from 'src/context/download-csv-context';
import { MerchantProfileContext } from 'src/context/merchant-profile-context';
import { getOltpTransactions } from 'src/services/oltp-transaction.service';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import TealiumEvents from 'src/helpers/tealium-events';
import { queue } from 'src/helpers/delay';

import './transaction-list.scss';

function acceptedTransaction(
  transaction,
  transactionType,
  responseCode,
  reference,
  fromAmount,
  toAmount,
) {
  const transactionAmountNumber = Number(transaction.transactionAmount);

  const tType = transactionType ? transaction.transactionTypeDescription === transactionType : true;
  const rCode = responseCode ? transaction.responseCodeClassifier === responseCode : true;
  const tRef = reference ? transaction.retrievalReferenceNumber.includes(reference) : true;
  const tBounds =
    !!fromAmount && !!toAmount
      ? transactionAmountNumber >= fromAmount && transactionAmountNumber <= toAmount
      : true;

  return tType && rCode && tRef && tBounds;
}

function TransactionList(props) {
  const handleTechnicalError = useTechnicalErrorHandler();
  const { transactionFilter } = useContext(TransactionFilterContext);
  const { setTransactionTotalPages, setLastLoadedPage, setCurrentTransactions, setCompanyName } =
    useContext(DownloadCSVContext);
  const [, setSelectedTransaction] = useContext(SelectedTransactionContext);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [miniLoader, setMiniLoader] = useState(false);
  const { merchant } = useContext(MerchantProfileContext);

  const [displayIncrements] = useState(5);
  const [displayedAmount, setDisplayedAmount] = useState(displayIncrements);

  let areSearchResults =
    (transactionFilter.transactionSearch.fromAmount &&
      transactionFilter.transactionSearch.toAmount) ||
    transactionFilter.transactionSearch.reference;

  const loadTransactions = async (isDateChangeOrInitialLoad, doBackgroundLoad = false) => {
    if (isDateChangeOrInitialLoad) {
      setCurrentPage(1);
      setDisplayedAmount(displayIncrements);
    }
    setLoading(!doBackgroundLoad);
    setMiniLoader(doBackgroundLoad);
    const transactionFetchAbortController = new AbortController();
    const transactionsResultWithTotalPages = await getOltpTransactions(
      transactionFilter.selectedDates[0],
      transactionFilter.selectedDates[1],
      isDateChangeOrInitialLoad ? 1 : currentPage,
      transactionFetchAbortController,
      handleTechnicalError,
      false,
      true,
    );

    const transactionsResult = Environment.current?.disabledFeatures?.pagination
      ? transactionsResultWithTotalPages
      : transactionsResultWithTotalPages.Rows;
    setTotalPages(transactionsResultWithTotalPages.NumberOfPages);
    setTransactionTotalPages(transactionsResultWithTotalPages.NumberOfPages);
    setLastLoadedPage(currentPage);
    if (!transactionFetchAbortController.signal.aborted) {
      if (transactionsResult && Array.isArray(transactionsResult)) {
        const fullTransactionList = isDateChangeOrInitialLoad
          ? transactionsResult
          : transactions.concat(transactionsResult);
        setTransactions(fullTransactionList);
        setFilteredTransactions(fullTransactionList);
        setCurrentTransactions(transactions);
        setCompanyName(merchant.companyName);
      } else {
        setTransactions([]);
        setFilteredTransactions([]);
      }
      setLoading(false);
      setMiniLoader(false);
    }
  };

  useEffect(() => {
    if (initialLoadDone) {
      loadTransactions(true);
    }
  }, [transactionFilter.selectedDates]);

  useEffect(() => {
    TealiumEvents.terminalTransactionPageLoad();
    if (transactions.length <= 0) {
      setInitialLoadDone(true);
      queue.push(() => loadTransactions(true));
    }

    props.createFilterItems(addDefaultFilters());
  }, []);

  useEffect(() => {
    if (!isLoading && transactions.length > 0) {
      const filtered = getFilterItems();
      props.createFilterItems(filtered);
    }
  }, [isLoading]);

  const getFilterItems = () => {
    const transactionTypes = [];
    const responseCodes = [];

    transactionTypes.push('Transaction Type');
    responseCodes.push('Response Codes');

    transactions.forEach((transaction) => {
      transactionTypes.push(transaction.transactionTypeDescription);
      responseCodes.push(transaction.responseCodeClassifier);
    });

    return {
      transactionTypes: [...new Set(transactionTypes)],
      responseCodes: [...new Set(responseCodes)],
    };
  };

  const filterTransactions = (transactionType, responseCode, reference, fromAmount, toAmount) => {
    if (transactions && transactions.length > 0) {
      const fTrans = transactions.filter((transaction) =>
        acceptedTransaction(
          transaction,
          transactionType,
          responseCode,
          reference,
          fromAmount,
          toAmount,
        ),
      );

      setFilteredTransactions(fTrans);
      setDisplayedAmount(displayIncrements);
    }
  };

  const addDefaultFilters = () => {
    const transactionTypes = [];
    const responseCodes = [];

    transactionTypes.push('Transaction Type');
    responseCodes.push('Response Codes');

    return {
      transactionTypes: [...new Set(transactionTypes)],
      responseCodes: [...new Set(responseCodes)],
    };
  };

  useEffect(() => {
    if (currentPage > 1) {
      loadTransactions(false, true);
    }
  }, [currentPage]);

  useEffect(() => {
    if (!isLoading && !miniLoader) {
      const trType =
        transactionFilter.dropdownFilters.transactionType !== 'Transaction Type'
          ? transactionFilter.dropdownFilters.transactionType
          : undefined;
      const resCode =
        transactionFilter.dropdownFilters.responseCode !== 'Response Codes'
          ? transactionFilter.dropdownFilters.responseCode
          : undefined;
      const reference =
        transactionFilter.transactionSearch.reference !== ''
          ? transactionFilter.transactionSearch.reference
          : undefined;
      const fromAmount = transactionFilter.transactionSearch.fromAmount || undefined;
      const toAmount = transactionFilter.transactionSearch.toAmount || undefined;

      filterTransactions(trType, resCode, reference, fromAmount, toAmount);
    }
  }, [
    transactionFilter.dropdownFilters,
    transactionFilter.transactionSearch,
    isLoading,
    miniLoader,
  ]);

  const noTransactionsFound =
    filteredTransactions && Array.isArray(filteredTransactions)
      ? filteredTransactions.length === 0
      : true;

  const observer = useRef();
  const nthElementRef = useCallback(
    (ref) => {
      if (isLoading || miniLoader) return;
      if (observer.current) {
        observer.current.disconnect();
      }
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
          }
        }
      });
      if (ref) observer.current.observe(ref);
    },
    [isLoading, miniLoader, currentPage, totalPages],
  );

  const observerMobile = useRef();
  const nthElementRefMobile = useCallback(
    (ref) => {
      if (isLoading || miniLoader) return;
      if (observerMobile.current) {
        observerMobile.current.disconnect();
      }
      observerMobile.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
          }
        }
      });
      if (ref) observerMobile.current.observe(ref);
    },
    [isLoading, miniLoader, currentPage, totalPages],
  );

  const isPaginationElement = (currentIndex, totalLength) =>
    currentIndex + 1 === Math.round(totalLength * 0.6);

  const mapTransactionTable = (transactionList) => {
    return transactionList.map((transaction, tIndex) => (
      <tr
        key={tIndex}
        ref={isPaginationElement(tIndex, transactionList.length) ? nthElementRef : null}
      >
        <td>{transaction.transactionTypeDescription}</td>
        <td>{transaction.retrievalReferenceNumber}</td>
        <td>{DateTime.fromISO(transaction.transactionDate).toFormat('dd/MM/yyyy')}</td>
        <td>{DateTime.fromISO(transaction.transactionDate).toFormat('HH:mm')}</td>
        <td>R {formatCurrencyAmountSimple(transaction.transactionAmount)}</td>
        <td>{transaction.PAN}</td>
        <td>
          <StatusSpan status={transaction.reconciliationStatus} />
        </td>
        <td>
          <span
            className="icon-chevron-right-thin is-icon-4 has-text-grey-light is-pulled-right"
            onClick={() => {
              TealiumEvents.terminalTransactionsClickMoreTransactionInformation();
              setSelectedTransaction(transaction);
            }}
          ></span>
        </td>
      </tr>
    ));
  };

  return (
    <div
      className={
        'TransactionList ' + (!isLoading && filteredTransactions.length > 0 ? 'min-data-width' : '')
      }
    >
      <div className="transaction-table-container" data-testid="transaction-table-container">
        {areSearchResults && (
          <>
            <span className="search-results-title is-hidden-mobile">
              Showing {Math.min(displayedAmount, filteredTransactions.length)} of{' '}
              {filteredTransactions.length} results for &nbsp;
              <b>
                {transactionFilter.transactionSearch.reference
                  ? `Reference no. ${transactionFilter.transactionSearch.reference}`
                  : `Amounts: R ${formatCurrencyAmountSimple(
                      transactionFilter.transactionSearch.fromAmount,
                    )} - R ${formatCurrencyAmountSimple(
                      transactionFilter.transactionSearch.toAmount,
                    )}`}
              </b>
            </span>
          </>
        )}

        <div className="is-hidden-tablet">
          {filteredTransactions &&
            filteredTransactions.map((transaction, index) => {
              return (
                <table key={`transaction-${index}`} className="table transaction-details-table">
                  <tbody>
                    <tr
                      ref={
                        isPaginationElement(index, filteredTransactions.length)
                          ? nthElementRefMobile
                          : null
                      }
                    >
                      <td className="table-data-sub-text">
                        {DateTime.fromISO(transaction.transactionDate).toFormat('HH:mm')}
                      </td>
                      <td className="is-pulled-right" style={{ marginRight: '5px' }}>
                        R {formatCurrencyAmountSimple(transaction.transactionAmount)}
                      </td>
                    </tr>
                    <tr>
                      <td className="table-data-description">
                        {transaction.transactionTypeDescription}
                      </td>
                      <td className="is-pulled-right is-vcentered">
                        <StatusSpan
                          status={transaction.reconciliationStatus}
                          className="is-pulled-left"
                        />
                        <span
                          className="icon-chevron-right-thin is-icon-5 has-text-grey-light is-pulled-right"
                          onClick={() => {
                            TealiumEvents.terminalTransactionsClickMoreTransactionInformation();
                            setSelectedTransaction(transaction);
                          }}
                        ></span>
                      </td>
                    </tr>
                    <tr>
                      <td className="table-data-sub-text">
                        Ref no. {transaction.retrievalReferenceNumber}
                      </td>
                    </tr>
                  </tbody>
                </table>
              );
            })}

          <ShowIf condition={miniLoader}>
            <div className="mini-loader">
              <LoaderAnimation />
            </div>
          </ShowIf>

          {isLoading && (
            <table className="table transaction-details-table">
              <tbody>
                <tr>
                  <td className="has-text-centered table-data-description">Loading...</td>
                </tr>
              </tbody>
            </table>
          )}

          {!isLoading && !areSearchResults && noTransactionsFound && (
            <table className="table is-fullwidth">
              <tbody>
                <tr>
                  <td className="has-text-centered no-data-image-container">
                    <img src={CactusImage} className="no-data-image" />
                  </td>
                </tr>
                <tr>
                  <td className="has-text-centered no-data-text-container">
                    <span className="title">No data to display</span>
                  </td>
                </tr>
              </tbody>
            </table>
          )}

          {!isLoading && areSearchResults && noTransactionsFound && (
            <table className="table is-fullwidth">
              <tbody>
                <tr>
                  <td className="has-text-centered no-data-image-container">
                    <img src={FallenIceCream} className="no-data-image" />
                  </td>
                </tr>
                <tr>
                  <td colSpan="7" className="has-text-centered no-data-text-container">
                    <span className="title">
                      Oh no, we couldn’t find any results for your search!
                    </span>
                    <br />
                    <span className="subtitle">Try adjusting your search criteria</span>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        <Card stretchVertical={true} className="card-table is-hidden-mobile">
          <table className="table transactions-table is-fullwidth">
            {!isLoading && (
              <thead>
                <tr>
                  <th>Transaction type</th>
                  <th>Retrieval reference</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Amount</th>
                  <th>PAN</th>
                  <th>Status</th>
                  <th className="has-text-right">Details</th>
                </tr>
              </thead>
            )}
            <tbody>
              {!isLoading &&
                filteredTransactions.length > 0 &&
                mapTransactionTable(filteredTransactions)}
              {!isLoading && !areSearchResults && noTransactionsFound && (
                <>
                  <tr>
                    <td colSpan="8" className="has-text-centered no-data-image-container">
                      <img src={CactusImage} className="no-data-image" />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="8" className="has-text-centered no-data-text-container">
                      <span className="title">No data to display</span>
                    </td>
                  </tr>
                </>
              )}
              {!isLoading && areSearchResults && noTransactionsFound && (
                <>
                  <tr>
                    <td colSpan="8" className="has-text-centered no-data-image-container">
                      <img src={FallenIceCream} className="no-data-image" />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="8" className="has-text-centered no-data-text-container">
                      <span className="title">
                        Oh no, we couldn’t find any results for your search!
                      </span>
                      <br />
                      <span className="subtitle">Try adjusting your search criteria</span>
                    </td>
                  </tr>
                </>
              )}
              {isLoading && (
                <tr>
                  <td colSpan="8" className="has-text-centered">
                    Loading...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <ShowIf condition={miniLoader}>
            <div className="mini-loader">
              <LoaderAnimation />
            </div>
          </ShowIf>
        </Card>
      </div>
    </div>
  );
}

export default TransactionList;
